// src/components/Header/PlanModal.tsx
import React, { useEffect, useMemo } from 'react';
import { X, TrendingUp, Target, Calendar, CheckCircle2, Layers, Flame, Rocket } from 'lucide-react';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { fetchActiveSprint, fetchSprints } from '../../store/sprintSlice';
import { fetchTasks } from '../../store/taskSlice';
import { TaskStatus } from '../../types/common';
import './Modals.scss';

interface PlanModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PlanModal: React.FC<PlanModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useAppDispatch();

  const { currentBoard } = useAppSelector((state) => state.boards);
  const { currentWorkspace } = useAppSelector((state) => state.workspaces);
  const { sprints, activeSprint } = useAppSelector((state) => state.sprints);
  const { tasks } = useAppSelector((state) => state.tasks);

  // Fetch real data when modal opens if board is set
  useEffect(() => {
    if (isOpen && currentBoard?.id) {
      dispatch(fetchActiveSprint(currentBoard.id));
      dispatch(fetchSprints(currentBoard.id));
      if (tasks.length === 0) {
        dispatch(fetchTasks({ boardId: currentBoard.id }));
      }
    }
  }, [isOpen, currentBoard?.id, dispatch, tasks.length]);

  // Determine current active sprint or fallback to latest sprint
  const currentActiveSprint = useMemo(() => {
    if (activeSprint) return activeSprint;
    return sprints.find((s) => s.status === 'ACTIVE' || s.isActive) || sprints[0] || null;
  }, [activeSprint, sprints]);

  // Calculate real metrics
  const metrics = useMemo(() => {
    if (currentActiveSprint) {
      const sprintTasks = tasks.filter((t) => String(t.sprintId) === String(currentActiveSprint.id));
      const effectiveTasks = sprintTasks.length > 0 ? sprintTasks : tasks;

      const totalTasksCount = effectiveTasks.length;
      const completedTasksCount = effectiveTasks.filter(
        (t) => t.status === TaskStatus.DONE || (t.status as any) === 'done'
      ).length;
      const inProgressTasksCount = effectiveTasks.filter(
        (t) => t.status === TaskStatus.IN_PROGRESS || (t.status as any) === 'in_progress'
      ).length;
      const todoTasksCount = Math.max(0, totalTasksCount - completedTasksCount - inProgressTasksCount);

      const progressPercent = totalTasksCount > 0 ? Math.round((completedTasksCount / totalTasksCount) * 100) : 0;

      // Story points
      const totalSP = effectiveTasks.reduce((sum, t) => sum + (t.storyPoints || 0), 0);
      const completedSP = effectiveTasks
        .filter((t) => t.status === TaskStatus.DONE || (t.status as any) === 'done')
        .reduce((sum, t) => sum + (t.storyPoints || 0), 0);

      // Days remaining
      let daysRemaining = 0;
      if (currentActiveSprint.endDate) {
        const endDate = new Date(currentActiveSprint.endDate).getTime();
        const now = Date.now();
        daysRemaining = Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
      }

      // Velocity: completed SP across completed sprints or current completed SP
      const completedSprints = sprints.filter((s) => s.status === 'COMPLETED');
      let velocity = currentActiveSprint.velocity || 0;
      if (!velocity && completedSprints.length > 0) {
        const totalCompletedSP = completedSprints.reduce(
          (sum, s) => sum + (s.completedStoryPoints || s.totalStoryPoints || 0),
          0
        );
        velocity = Math.round(totalCompletedSP / completedSprints.length);
      }
      if (!velocity) {
        velocity = completedSP > 0 ? completedSP : 0;
      }

      return {
        isSprintActive: currentActiveSprint.status === 'ACTIVE' || currentActiveSprint.isActive,
        sprintTitle: currentActiveSprint.name || 'Current Sprint',
        sprintGoal: currentActiveSprint.goal || null,
        progressPercent,
        completedTasksCount,
        totalTasksCount,
        inProgressTasksCount,
        todoTasksCount,
        completedSP,
        totalSP,
        daysRemaining,
        velocity,
        startDate: currentActiveSprint.startDate
          ? new Date(currentActiveSprint.startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
          : null,
        endDate: currentActiveSprint.endDate
          ? new Date(currentActiveSprint.endDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
          : null,
      };
    } else {
      // Board-wide fallback when no sprint is active
      const totalTasksCount = tasks.length;
      const completedTasksCount = tasks.filter(
        (t) => t.status === TaskStatus.DONE || (t.status as any) === 'done'
      ).length;
      const inProgressTasksCount = tasks.filter(
        (t) => t.status === TaskStatus.IN_PROGRESS || (t.status as any) === 'in_progress'
      ).length;
      const todoTasksCount = Math.max(0, totalTasksCount - completedTasksCount - inProgressTasksCount);

      const progressPercent = totalTasksCount > 0 ? Math.round((completedTasksCount / totalTasksCount) * 100) : 0;

      const totalSP = tasks.reduce((sum, t) => sum + (t.storyPoints || 0), 0);
      const completedSP = tasks
        .filter((t) => t.status === TaskStatus.DONE || (t.status as any) === 'done')
        .reduce((sum, t) => sum + (t.storyPoints || 0), 0);

      return {
        isSprintActive: false,
        sprintTitle: currentBoard?.name ? `${currentBoard.name} Overview` : 'Project Overview',
        sprintGoal: null,
        progressPercent,
        completedTasksCount,
        totalTasksCount,
        inProgressTasksCount,
        todoTasksCount,
        completedSP,
        totalSP,
        daysRemaining: 0,
        velocity: completedSP,
        startDate: null,
        endDate: null,
      };
    }
  }, [currentActiveSprint, tasks, sprints, currentBoard?.name]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content plan-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="plan-header-title">
            <div className="plan-header-badges">
              {currentWorkspace && <span className="workspace-badge">{currentWorkspace.name}</span>}
              {currentBoard && <span className="board-badge">{currentBoard.name}</span>}
              {metrics.isSprintActive ? (
                <span className="active-sprint-badge">ACTIVE SPRINT</span>
              ) : (
                <span className="no-sprint-badge">BOARD OVERVIEW</span>
              )}
            </div>
            <h2>{metrics.sprintTitle}</h2>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close modal">
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {/* Active Sprint Goal notice if present */}
          {metrics.sprintGoal && (
            <div className="plan-goal-banner">
              <Target className="goal-icon" size={18} />
              <div>
                <strong>Sprint Goal:</strong> {metrics.sprintGoal}
              </div>
            </div>
          )}

          {/* Stats Grid */}
          <div className="plan-stats">
            {/* 1. Sprint Progress */}
            <div className="plan-stat-card">
              <div className="stat-card-header">
                <TrendingUp className="stat-icon" size={20} />
                <span className="stat-badge">{metrics.progressPercent}%</span>
              </div>
              <h3>Sprint Progress</h3>
              <p className="stat-value">{metrics.progressPercent}%</p>
              <div className="stat-progress-bar">
                <div className="stat-progress-fill" style={{ width: `${metrics.progressPercent}%` }} />
              </div>
              <p className="stat-description">
                {metrics.completedTasksCount} of {metrics.totalTasksCount} tasks completed
              </p>
            </div>

            {/* 2. Scope & Story Points */}
            <div className="plan-stat-card">
              <div className="stat-card-header">
                <Target className="stat-icon" size={20} />
                <Layers className="stat-icon-secondary" size={16} />
              </div>
              <h3>Story Points Scope</h3>
              <p className="stat-value">
                {metrics.completedSP} / {metrics.totalSP} <span className="unit-label">SP</span>
              </p>
              <p className="stat-description">
                {metrics.totalSP > 0
                  ? `${Math.round((metrics.completedSP / metrics.totalSP) * 100)}% points delivered`
                  : 'Total story points in scope'}
              </p>
            </div>

            {/* 3. Days Remaining */}
            <div className="plan-stat-card">
              <div className="stat-card-header">
                <Calendar className="stat-icon" size={20} />
              </div>
              <h3>Days Remaining</h3>
              <p className="stat-value">{metrics.daysRemaining}</p>
              <p className="stat-description">
                {metrics.startDate && metrics.endDate
                  ? `${metrics.startDate} – ${metrics.endDate}`
                  : metrics.isSprintActive
                  ? 'Until sprint completion'
                  : 'No active sprint schedule'}
              </p>
            </div>

            {/* 4. Team Velocity */}
            <div className="plan-stat-card">
              <div className="stat-card-header">
                <Flame className="stat-icon" size={20} />
              </div>
              <h3>Team Velocity</h3>
              <p className="stat-value">{metrics.velocity} <span className="unit-label">SP</span></p>
              <p className="stat-description">Story points delivered</p>
            </div>
          </div>

          {/* Task Status Breakdown Bar */}
          <div className="plan-breakdown-section">
            <h4>Task Breakdown</h4>
            <div className="breakdown-pills">
              <span className="pill done">
                <CheckCircle2 size={13} /> {metrics.completedTasksCount} Done
              </span>
              <span className="pill in-progress">
                <Rocket size={13} /> {metrics.inProgressTasksCount} In Progress
              </span>
              <span className="pill todo">
                {metrics.todoTasksCount} To Do
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
